import { useState, useEffect } from 'react';
import axios from 'axios';
import { API, getAuthToken } from '@/App';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Trash2, Mail, Phone, Eye } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const statusColors = {
  new: 'bg-blue-100 text-blue-800',
  contacted: 'bg-yellow-100 text-yellow-800',
  booked: 'bg-green-100 text-green-800',
  closed: 'bg-gray-100 text-gray-800',
};

const AdminInquiries = () => {
  const [inquiries, setInquiries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedInquiry, setSelectedInquiry] = useState(null);

  useEffect(() => { fetchInquiries(); }, []);

  const fetchInquiries = async () => {
    const token = getAuthToken();
    try {
      const res = await axios.get(`${API}/admin/inquiries`, { headers: { Authorization: `Bearer ${token}` } });
      setInquiries(res.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = async (id, status) => {
    const token = getAuthToken();
    try {
      await axios.put(`${API}/admin/inquiries/${id}`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Updated!');
      fetchInquiries();
    } catch (error) {
      toast.error('Failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete?')) return;
    const token = getAuthToken();
    try {
      await axios.delete(`${API}/admin/inquiries/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Deleted!');
      fetchInquiries();
    } catch (error) {
      toast.error('Failed');
    }
  };

  const formatDate = (dateString) => new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

  if (isLoading) return <div className="flex items-center justify-center h-64"><div className="spinner"></div></div>;

  return (
    <div data-testid="admin-inquiries">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-heading text-3xl text-[#2C2C2C]">Inquiries</h1>
        <p className="text-[#6B7280]">{inquiries.length} total</p>
      </div>

      {inquiries.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-soft p-12 text-center">
          <Mail className="w-12 h-12 text-[#E8DFD0] mx-auto mb-4" />
          <p className="text-[#6B7280]">No inquiries yet</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-soft overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#F5F5F0]">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#6B7280]">Client</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#6B7280]">Event</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#6B7280]">Date</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#6B7280]">Status</th>
                  <th className="px-6 py-4 text-right text-sm font-medium text-[#6B7280]">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E8DFD0]">
                {inquiries.map((inquiry, index) => (
                  <tr key={inquiry.id}>
                    <td className="px-6 py-4">
                      <p className="font-medium text-[#2C2C2C]">{inquiry.name}</p>
                      <p className="text-sm text-[#6B7280]">{inquiry.email}</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-[#2C2C2C]">{inquiry.event_type}</p>
                      <p className="text-sm text-[#6B7280]">{inquiry.event_date}</p>
                    </td>
                    <td className="px-6 py-4 text-sm text-[#6B7280]">{formatDate(inquiry.created_at)}</td>
                    <td className="px-6 py-4">
                      <div>
                        <Select value={inquiry.status} onValueChange={(v) => handleStatusChange(inquiry.id, v)}>
                          <SelectTrigger className={`w-32 ${statusColors[inquiry.status]} border-0 rounded-full`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="new">New</SelectItem>
                            <SelectItem value="contacted">Contacted</SelectItem>
                            <SelectItem value="booked">Booked</SelectItem>
                            <SelectItem value="closed">Closed</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex gap-2 justify-end">
                        <Button variant="ghost" size="sm" onClick={() => setSelectedInquiry(inquiry)}><Eye size={14} /></Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(inquiry.id)} className="text-red-500"><Trash2 size={14} /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={!!selectedInquiry} onOpenChange={() => setSelectedInquiry(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          {selectedInquiry && (
            <>
              <DialogHeader><DialogTitle className="font-heading text-2xl">Inquiry Details</DialogTitle></DialogHeader>
              <div className="space-y-6 mt-4">
                <div className="flex items-center gap-4 p-4 bg-[#F5F5F0] rounded-xl">
                  <div className="w-12 h-12 rounded-full bg-[#D4AF37] flex items-center justify-center text-white font-heading text-xl">{selectedInquiry.name.charAt(0)}</div>
                  <div>
                    <h3 className="font-heading text-xl text-[#2C2C2C]">{selectedInquiry.name}</h3>
                    <div className="flex items-center gap-4 text-sm text-[#6B7280]">
                      <span className="flex items-center gap-1"><Mail size={14} /> {selectedInquiry.email}</span>
                      {selectedInquiry.phone && <span className="flex items-center gap-1"><Phone size={14} /> {selectedInquiry.phone}</span>}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><p className="text-sm text-[#6B7280]">Event Type</p><p className="font-medium">{selectedInquiry.event_type}</p></div>
                  <div><p className="text-sm text-[#6B7280]">Event Date</p><p className="font-medium">{selectedInquiry.event_date}</p></div>
                  {selectedInquiry.venue && <div><p className="text-sm text-[#6B7280]">Venue</p><p className="font-medium">{selectedInquiry.venue}</p></div>}
                  {selectedInquiry.guest_count && <div><p className="text-sm text-[#6B7280]">Guest Count</p><p className="font-medium">{selectedInquiry.guest_count}</p></div>}
                  {selectedInquiry.budget_range && <div><p className="text-sm text-[#6B7280]">Budget Range</p><p className="font-medium">{selectedInquiry.budget_range}</p></div>}
                  {selectedInquiry.how_heard && <div><p className="text-sm text-[#6B7280]">How They Heard</p><p className="font-medium">{selectedInquiry.how_heard}</p></div>}
                </div>
                {selectedInquiry.vision && <div><p className="text-sm text-[#6B7280] mb-2">Vision</p><p className="text-[#2C2C2C] bg-[#F5F5F0] p-4 rounded-xl">{selectedInquiry.vision}</p></div>}
                {selectedInquiry.message && <div><p className="text-sm text-[#6B7280] mb-2">Message</p><p className="text-[#2C2C2C] bg-[#F5F5F0] p-4 rounded-xl">{selectedInquiry.message}</p></div>}
                <div className="pt-4 border-t flex gap-4">
                  <Button onClick={() => setSelectedInquiry(null)} className="flex-1 bg-[#D4AF37] hover:bg-[#C5A028] text-white rounded-full">Close</Button>
                  <Button variant="outline" onClick={() => window.open(`mailto:${selectedInquiry.email}`)} className="flex-1 border-[#4A5D23] text-[#4A5D23] rounded-full"><Mail size={16} className="mr-2" /> Email</Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminInquiries;
